// `/letter` IS THE APP'S REVIEW & EXPORT, IN THE APP'S OWN WORDS (WEB-PARITY-6, R221). Tyler, 2026-09-29:
// "the site should replicate exactly what the app has at this time".
//
// Every label, heading, footer, button and alert on `/letter` that the server does not send is the app's own string,
// copied unchanged from the line it cites (under `AppealAce/`), as `parity3Wording.ts` is for steps 2 and 3. A `{name}`
// is the app's own interpolation written the web's way. They carry no new approval: the approval is Tyler's standing
// instruction (`WEB_PARITY`), and `test/letterReview.test.tsx` reads each cited line and fails when the words differ.
//
// ADAPTED, AND SAID SO (`cut`): three of the app's lines pair a sentence that is true on the website with one that is
// not — "Your contact info saves to your account for next time": the website keeps no profile (since WEB-PARITY-5 it holds
// the contact given on this page for the tab only, ../../app/contact.ts). The true sentence is kept word for word and the
// false one is cut;
// the test checks the kept words are inside the cited literal and that the cut is named.
//
// NOT HERE, ON PURPOSE (each is a finding in docs/WEB_READINESS.md's WEB-PARITY-6 section):
//   - the deadline reminder's buttons and promises (ReviewView.swift:1102-1278) and the outcome reminder's ask
//     (:384-389): the app reminds by phone notification, which a website cannot send; D8's reminder half is Tyler's;
//   - "Tap to read & edit" (:1343): the app's row edits nothing (D1, LT-3) — the website does not repeat the promise;
//   - "Save it to Files, email it, or print. Tap Edit to make changes first." (LetterPreviewSheet.swift:92): Files is the
//     iPhone's; a browser saves where it saves;
//   - "Read our full {state} appeal guide" (:722-725) and the protest-window row (:656-659): no served field carries
//     either for this screen;
//   - "Also email me when next year's deadline is coming." (EmailCopyRow.swift:169, since R175 `AssentCopy.emailReminderConsent`, AssentCopy.swift:70) and its promise: D8's reminders.

import { WEB_PARITY, type AppWording } from "../../components/heldCopy";

/** The app's words with a sentence cut from beside them. `cut` names what was cut and why. */
export interface AdaptedAppWording extends AppWording {
  readonly cut: string;
}

function app(appSource: string, text: string): AppWording {
  return { text, status: "appWording", approval: WEB_PARITY, appSource };
}

function adapted(appSource: string, text: string, cut: string): AdaptedAppWording {
  return { text, status: "appWording", approval: WEB_PARITY, appSource, cut };
}

const RV = "Features/Review/ReviewView.swift";
const ILP = "Features/Review/InlineLetterPreview.swift";
const LFS = "Features/Review/LetterFeedbackSheet.swift";
const LPS = "Features/Review/LetterPreviewSheet.swift";
const LCS = "Features/Review/LetterChangeSheet.swift";
const ECR = "Features/Review/EmailCopyRow.swift";
const SE = "Features/Tabs/SignatureEditor.swift";

const CONTACT_CUT = "the first sentence, about contact info saving to an account: the website keeps no profile, it holds the contact given on Review for this tab only";

export const REVIEW_WORDING = {
  // ══ the screen and its three phases ══
  "review.nav": app(`${RV}:253`, "Review & Export"),
  "review.phase1.title": app(`${RV}:128`, "Your letter is ready"),
  "review.phase1.subtitle": app(`${RV}:129`, "Preview the PDF and download or share it."),
  "review.phase2.title": app(`${RV}:144`, "Copy out and file"),
  "review.phase2.subtitle": app(`${RV}:145`, "Everything here is final. To change anything, use \"I need a change to this letter\" above."),
  "review.phase3.title": app(`${RV}:214`, "After you've filed"),
  "review.phase3.subtitle": app(`${RV}:215`, "File the PDF with your county, then come back to log the result and track your savings."),

  // ══ before you export: contact and signature (LT-11) ══
  "review.setup.header.before": app(`${RV}:608`, "Before you export"),
  "review.setup.header.signature": app(`${RV}:608`, "Signature"),
  "review.setup.contact.title": app(`${RV}:587`, "Add your contact info"),
  "review.setup.contact.subtitle": app(`${RV}:588`, "Name and mailing address — appears on every letter you export"),
  "review.setup.sign.title": app(`${RV}:598`, "Sign this appeal"),
  "review.setup.sign.subtitle": app(`${RV}:599`, "Added to this letter's PDF only — you sign each appeal yourself"),
  "review.setup.signed.title": app(`${RV}:602`, "Signature added"),
  "review.setup.signed.subtitle": app(`${RV}:603`, "Tap to redo or restyle it"),
  "review.setup.footer": adapted(`${RV}:610`, "Your signature is added to this appeal's letter only — it isn't saved or reused.", CONTACT_CUT),

  // ══ the reduction hero ══
  "review.hero.label": app(`${RV}:542`, "You're requesting"),
  "review.hero.amount": app(`${RV}:547`, "{amount} off"),
  "review.hero.percent": app(`${RV}:552`, "That's −{percent}% off your **assessed** value."),
  "review.hero.footer": app(`${RV}:560`, "If the board grants your requested value. Not a guarantee."),

  // ══ the letter paper (InlineLetterPreview) and the change request ══
  "preview.brand.pro": app(`${ILP}:41`, "PRO"),
  "preview.brand.ppeal": app(`${ILP}:42`, "ppeal"),
  "preview.notice": app(`${ILP}:45`, "NOTICE OF {term}"),
  "preview.comp": app(`${ILP}:66`, "COMP {n}"),
  "preview.unsigned": app(`${ILP}:85`, "—"),
  "preview.open": app(`${ILP}:100`, "See my Appeal Letter"),
  "preview.a11y": app(`${ILP}:125`, "Appeal letter preview. Tap to open the full PDF."),
  "review.signedCaption": app(`${RV}:662`, "Signed · {date}"),
  "review.change.title": app(`${LCS}:72`, "I need a change to this letter"),
  "review.change.subtitle": app(`${LCS}:41`, "Wording, a number, something about your home we missed — tell us and we'll fix it."),
  "review.change.working": app(`${RV}:913`, "Making your changes…"),

  // ══ the letter is behind the appeal (`letterIsStale`, served as `review.stale`; F-WP6-2) ══
  "review.stale.footer": app(`${RV}:999`, "You've edited this appeal — regenerate to update the letter before you file."),
  "review.stale.title": app(`${RV}:329`, "Your edits aren't in the letter yet"),
  "review.stale.revise": app(`${RV}:330`, "Revise from your details"),
  "review.stale.exportAsIs": app(`${RV}:331`, "Export it as it is"),
  "review.stale.message": app(
    `${RV}:333`,
    "You've changed your appeal since this letter was written, so the PDF won't reflect those edits. Revise from your details to rebuild it, or export the current version as it is.",
  ),

  // ══ worth a second look; the filing deadline ══
  "review.audit.header": app(`${RV}:1031`, "Worth a second look"),
  "review.audit.footer": app(`${RV}:1033`, "These are advisory — your letter is ready to file either way."),
  "review.deadline.header": app(`${RV}:1232`, "Filing deadline"),

  // ══ copy out and file ══
  "review.revise.title": app(`${LCS}:34`, "Revise from your details"),
  "review.revise.subtitle": app(`${LCS}:43`, "Step back through your details, comps, and value, then come back here."),
  "review.revise.footer": app(
    `${LCS}:53`,
    "Nothing is re-charged — your credit, the comparable sales you already pulled, and this letter all stay as they are until you rebuild it.",
  ),
  "review.letter.header": app(`${RV}:189`, "Appeal letter"),
  "review.letter.placeholder": app(`${RV}:173`, "Your letter will appear here once generated."),
  "review.letter.copy": app(`${RV}:180`, "Copy letter as text"),
  "review.copied": app(`${RV}:180`, "Copied!"),
  "review.letter.footer": app(
    `${RV}:199`,
    "This is what files as your PDF. Copy it to paste into a county online portal or email — it updates whenever you rebuild the letter.\n\nYou can also paste it into Word or Pages and edit the wording yourself. If you do, file that document rather than the PDF here, which won't have your changes — and still export the PDF, so you can attach Exhibit A, the comparable-sales grid your letter refers to.",
  ),
  "review.letter.read": app(`${LFS}:106`, "Read my letter"),
  "review.letter.hide": app(`${LFS}:106`, "Hide my letter"),
  "review.summary.header": app(`${RV}:1389`, "Portal summary"),
  "review.summary.placeholder": app(`${RV}:1377`, "A short summary for your county's online form."),
  "review.summary.copy": app(`${RV}:1384`, "Copy summary"),
  "review.summary.footer": app(
    `${RV}:1391`,
    "Paste this into your county portal's summary field (most cap around 1,000 characters). The PDF letter still attaches separately.",
  ),

  // ══ after you've filed ══
  "review.file.header": app(`${RV}:716`, "How to file"),
  "review.file.who": app(`${RV}:681`, "Who to file with"),
  "review.file.how.title": app(`${RV}:683`, "How to submit"),
  "review.file.how.detail": app(
    `${RV}:684`,
    "By mail, your county's online portal, or in person — check your assessment notice or county website for which your county accepts.",
  ),
  "review.file.deadline": app(`${RV}:686`, "Deadline"),
  "review.file.attach": app(`${RV}:702`, "What to attach"),
  "review.file.attach.notice": app(`${RV}:783`, "A copy of your assessment notice or tax bill"),
  "review.file.attach.ownership": app(`${RV}:784`, "Proof of ownership (deed or closing statement), if your county asks for it"),
  "review.file.attach.form": app(`${RV}:785`, "Check your notice or county website — some counties require their own appeal form in addition to a letter"),
  "review.file.footer": app(
    `${RV}:718`,
    "General guidance, not a substitute for the instructions on your assessment notice — counties vary in what they require and how they accept appeals.",
  ),
  "review.outcome.header": app(`${RV}:821`, "Outcome"),
  "review.outcome.log": app(`${RV}:817`, "Log the outcome"),
  "review.outcome.update": app(`${RV}:817`, "Update outcome"),
  "review.outcome.footer.none": app(
    `${RV}:824`,
    "Once you've filed with your county, record whether it was granted — we'll track your savings, and (anonymously) it helps other homeowners.",
  ),
  "review.outcome.footer.some": app(`${RV}:825`, "Update this anytime as your appeal progresses."),
  "review.outcome.increased": app(`${RV}:803`, "Increased"),
  "review.outcome.saved": app(`${RV}:808`, "Saved {amount}"),
  "review.previous.header": app(`${RV}:1472`, "Previously exported"),
  "review.previous.footer": app(
    `${RV}:1474`,
    "Copies of letters you've already exported, newest first. Opening one downloads that version as it was — it doesn't change your current appeal. The last {n} are kept.",
  ),
  "review.delete": app(`${RV}:225`, "Delete this appeal"),
  "review.delete.footer": app(`${RV}:228`, "Removes this year's appeal from the property. Your other appeals stay."),
  "review.delete.alert.title": app(`${RV}:399`, "Delete this appeal?"),
  "review.delete.alert.message": app(`${RV}:406`, "This can't be undone. The exported PDF you may have already saved stays where it is."),
  "review.delete.alert.cancel": app(`${RV}:400`, "Cancel"),
  "review.delete.alert.confirm": app(`${RV}:401`, "Delete"),

  // ══ the export confirmation ══
  "review.export.title.both": app(`${RV}:642`, "Add contact info and signature first?"),
  "review.export.title.contact": app(`${RV}:643`, "Add your contact info first?"),
  "review.export.title.signature": app(`${RV}:644`, "Add your signature first?"),
  "review.export.addContact": app(`${RV}:312`, "Add contact info"),
  "review.export.sign": app(`${RV}:315`, "Sign this appeal"),
  "review.export.anyway": app(`${RV}:317`, "Export without it"),
  "review.export.cancel": app(`${RV}:318`, "Cancel"),
  "review.export.message": adapted(`${RV}:654`, "Your signature applies to this appeal's letter only.", CONTACT_CUT),

  // ══ the answer to a change request, and its two other alerts (the title and outcome line are the server's) ══
  "review.concern.takeMeThere": app(`${RV}:359`, "Take me there"),
  "review.concern.notNow": app(`${RV}:360`, "Not now"),
  "review.concern.rebuild": app(`${RV}:367`, "Rebuild my letter"),
  "review.concern.ok": app(`${RV}:370`, "OK"),
  "review.reviseError.title": app(`${RV}:377`, "We couldn't make that change"),
  "review.reviseError.network": app(
    `${RV}:480`,
    "We couldn't reach PROppeal's servers just now. Your letter is unchanged — try again in a moment.",
  ),
  "review.exhausted.title": app(`${RV}:386`, "Let's get someone to look at this"),
  "review.exhausted.support": app(`${RV}:387`, "Email support"),
  "review.exhausted.notNow": app(`${RV}:388`, "Not now"),
  "review.exhausted.message": app(
    `${RV}:390`,
    "You've used all {max} edits on this appeal. If the letter still isn't right, that's on us to fix — send it over and we'll help directly.",
  ),

  // ══ "Request a change" (LetterFeedbackSheet) ══
  "feedback.title": app(`${LFS}:146`, "Request a change"),
  "feedback.cancel": app(`${LFS}:151`, "Cancel"),
  "feedback.send": app(`${LFS}:154`, "Send"),
  "feedback.starters.header": app(`${LFS}:90`, "What's bothering you?"),
  "feedback.starter.missing": app(`${LFS}:44`, "Something about my home is missing"),
  "feedback.starter.missing.seed": app(`${LFS}:44`, "The letter doesn't mention "),
  "feedback.starter.wrong": app(`${LFS}:45`, "A number or detail looks wrong"),
  "feedback.starter.wrong.seed": app(`${LFS}:45`, "This looks wrong to me: "),
  "feedback.starter.weak": app(`${LFS}:46`, "It doesn't feel strong enough"),
  "feedback.starter.weak.seed": app(`${LFS}:46`, "This reads weaker than my case actually is because "),
  "feedback.starter.unclear": app(`${LFS}:47`, "I don't understand part of it"),
  "feedback.starter.unclear.seed": app(`${LFS}:47`, "I don't understand this part: "),
  "feedback.starter.else": app(`${LFS}:48`, "Something else"),
  "feedback.starters.footer": app(`${LFS}:92`, "Tap one to start, or just write below."),
  "feedback.letter.header": app(`${LFS}:111`, "Your letter"),
  "feedback.letter.footer": app(
    `${LFS}:113`,
    "Touch and hold any sentence to select and copy it, then paste it below — that is the most exact way to tell us which part you mean.",
  ),
  "feedback.words.header": app(`${LFS}:132`, "In your own words"),
  "feedback.words.placeholder": app(
    `${LFS}:124`,
    "Be as specific as you can — which sentence, which number, what's missing. The more exact you are, the better the fix.",
  ),
  "feedback.words.footer": app(
    `${LFS}:138`,
    "We'll edit the letter where the wording is the problem. If what you're telling us isn't in your appeal yet — a condition issue, a wrong record — we'll say so and point you to the step that adds it, because a claim with nothing behind it is the kind of thing a board discounts.\n\n{remaining} edit{plural} left on this appeal.",
  ),

  // ══ the PDF (LetterPreviewSheet) and "Email me a copy" (EmailCopyRow) ══
  "pdf.title": app(`${LPS}:50`, "Your appeal letter"),
  "pdf.edit": app(`${LPS}:57`, "Edit"),
  "pdf.download": app(`${LPS}:73`, "Download / Share PDF"),
  // WALK-FIX-1: the ✕ that closes the sheet is named by the app's own word for closing a sheet, "Close" (the "Before you
  // build" sheet's, GenerateView.swift:1724). The app's preview sheet has no Done; its "Edit" stays, and the ✕ shows no
  // word of its own, only this name, which a screen reader reads.
  "pdf.close": app("Features/Generate/GenerateView.swift:1724", "Close"),
  "email.open": app(`${ECR}:99`, "Email me a copy"),
  "email.placeholder": app(`${ECR}:112`, "you@example.com"),
  "email.send": app(`${ECR}:134`, "Send"),
  "email.promise": app(`${ECR}:70`, "We'll use your address to send this PDF. Nothing else."),
  "email.sending": app(`${ECR}:178`, "Sending…"),
  "email.sent": app(`${ECR}:191`, "Sent to {to}."),
  "email.tryAgain": app(`${ECR}:201`, "Try again"),

  // ══ "Sign your appeal" (SignatureEditor) ══
  "sign.title": app(`${SE}:93`, "Sign your appeal"),
  "sign.signing": app(`${SE}:37`, "You're signing {context}."),
  "sign.context": app(`${RV}:61`, "your {year} appeal letter for {place}"),
  "sign.context.noAddress": app(`${RV}:60`, "this property"),
  "sign.explain": app(
    `${SE}:40`,
    "Your signature is added to this letter's PDF only. It isn't saved to your account or reused for any other appeal — you sign each appeal yourself.",
  ),
  "sign.style": app(`${SE}:45`, "Signature style"),
  "sign.draw": app(`${SE}:18`, "Draw"),
  "sign.type": app(`${SE}:18`, "Type"),
  "sign.clear": app(`${SE}:54`, "Clear"),
  "sign.save": app(`${SE}:65`, "Save signature"),
  "sign.update": app(`${SE}:65`, "Update signature"),
  "sign.signed": app(`${SE}:81`, "Signed for this appeal. Draw or type a new one above to replace it."),
  "sign.typed.placeholder": app(`${SE}:103`, "Type your full name"),
  "sign.typed.preview": app(`${SE}:111`, "Your signature"),
  "sign.typed.footer": app(`${SE}:129`, "Your typed name is styled as a signature for the letter. Switch to Draw if you'd rather sign by hand."),
  "sign.here": app(`${SE}:162`, "× sign here"),
} as const satisfies Record<string, AppWording>;

/** `Disclaimers.pdfFooter` (AppealAce/Shared/Disclaimers.swift:93-97), a multi-line Swift literal, with `legalEntity` in
 *  it as the app renders it. Pinned by `test/letterReview.test.tsx`, which joins the literal's lines as Swift does. */
export const PDF_FOOTER = {
  appSource: "Shared/Disclaimers.swift:93",
  text: "Prepared with PROppeal, a product of {legalEntity}. This document is not tax, legal, or appraisal advice and the comparable sales shown are not a certified appraisal. Verify all figures with your county before filing. No savings is guaranteed.",
} as const;

export type ReviewWordingId = keyof typeof REVIEW_WORDING;

export function word(id: ReviewWordingId): string {
  return REVIEW_WORDING[id].text;
}

/** A sentence with its `{name}` slots filled. A slot left unfilled is a thrown error, never printed braces. */
export function wordWith(id: ReviewWordingId, values: Readonly<Record<string, string | number>>): string {
  return word(id).replace(/\{(\w+)\}/g, (_, name: string) => {
    if (!Object.hasOwn(values, name)) throw new Error(`wordWith(${id}): no value for {${name}}`);
    return String(values[name]);
  });
}
